import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import AIManagersService from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';
import { AIIcon } from 'src/icons/AIIcon';
import { AI_MANAGERS_AVAILABLE } from 'src/constants/features';
import { normalizeColumnsAfterMove, SalesFunnelBoard } from 'src/screens/SalesFunnel/SalesFunnelBoard';

import { StreamSection } from './StreamPageSections';

import type {
    AIManager,
    AIManagerConversation,
    AIManagerFunnelStageKey,
    AIManagerKanban,
} from 'src/services/AIManagersService';
import type { Stream } from 'src/services/LeadStreamsService/agent';

export const managerStreamIds = (manager: AIManager) =>
    Array.from(
        new Set(
            [...(manager.leadStreamIds || []), ...(manager.leadStreams || []).map((stream) => stream.id)].map(Number)
        )
    );

export default function StreamManagerFunnel({ stream }: { stream: Stream }) {
    if (!AI_MANAGERS_AVAILABLE) {
        return (
            <StreamSection
                id="funnel"
                title="Воронка продаж"
            >
                <Stack
                    alignItems="flex-start"
                    spacing={2}
                >
                    <Typography color="text.secondary">
                        ИИ-менеджер сможет вести диалоги и обновлять стадии продаж.
                    </Typography>
                    <Button
                        variant="outlined"
                        disabled
                    >
                        Подключить ИИ-менеджера · Скоро
                    </Button>
                </Stack>
            </StreamSection>
        );
    }
    return <AvailableStreamManagerFunnel stream={stream} />;
}

function AvailableStreamManagerFunnel({ stream }: { stream: Stream }) {
    const history = useHistory();
    const [managers, setManagers] = useState<AIManager[]>([]);
    const [selectedId, setSelectedId] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [connectId, setConnectId] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [connectError, setConnectError] = useState('');
    const mounted = useRef(true);
    const request = useRef(0);
    const connected = managers.filter((manager) => managerStreamIds(manager).includes(stream.id));
    const available = managers.filter((manager) => !managerStreamIds(manager).includes(stream.id));
    const selected = connected.find((manager) => manager.id === selectedId) || connected[0];
    const load = useCallback(async () => {
        const version = ++request.current;
        setError('');
        setLoading(true);
        try {
            const data = await AIManagersService.getManagers();
            if (mounted.current && version === request.current) setManagers(data);
        } catch {
            if (mounted.current && version === request.current) setError('Не удалось загрузить ИИ-менеджеров.');
        } finally {
            if (mounted.current && version === request.current) setLoading(false);
        }
    }, []);
    useEffect(() => {
        mounted.current = true;
        void load();
        const subscription = EventService.subscribe('AI_MANAGERS_UPDATED', () => void load());
        return () => {
            mounted.current = false;
            ++request.current;
            subscription.unsubscribe();
        };
    }, [load]);
    const connect = async () => {
        if (!connectId || connecting) return;
        setConnecting(true);
        setConnectError('');
        try {
            const current = await AIManagersService.getManager(connectId);
            const updated = await AIManagersService.updateManager(connectId, {
                leadStreamIds: Array.from(new Set([...managerStreamIds(current), stream.id])),
            });
            EventService.dispatch('AI_MANAGERS_UPDATED');
            if (!mounted.current) return;
            setManagers((items) => [updated, ...items.filter((manager) => manager.id !== updated.id)]);
            setSelectedId(updated.id);
            setDialogOpen(false);
        } catch {
            if (mounted.current) setConnectError('Не удалось подключить менеджера. Попробуйте ещё раз.');
        } finally {
            if (mounted.current) setConnecting(false);
        }
    };
    const openConnect = () => {
        setConnectId(available[0]?.id || '');
        setConnectError('');
        setDialogOpen(true);
    };
    return (
        <StreamSection
            id="funnel"
            title="Воронка продаж"
        >
            {loading ? (
                <Box sx={{ py: 3 }}>
                    <CircularProgress
                        size={24}
                        aria-label="Загрузка менеджеров потока"
                    />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    action={<Button onClick={() => void load()}>Повторить</Button>}
                >
                    {error}
                </Alert>
            ) : !selected ? (
                <Stack
                    alignItems="flex-start"
                    spacing={2}
                    sx={{ py: 1 }}
                >
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <AIIcon
                            width={32}
                            height={32}
                        />
                        <Typography sx={{ fontWeight: 600 }}>Подключите ИИ-менеджера к потоку</Typography>
                    </Box>
                    <Typography color="text.secondary">Он будет вести диалоги и обновлять стадии продаж.</Typography>
                    <Button
                        variant="contained"
                        onClick={openConnect}
                    >
                        Подключить ИИ-менеджера
                    </Button>
                </Stack>
            ) : (
                <Stack spacing={2.5}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        gap={1.5}
                    >
                        {connected.length > 1 ? (
                            <TextField
                                select
                                size="small"
                                label="ИИ-менеджер"
                                value={selected.id}
                                onChange={(event) => setSelectedId(event.target.value)}
                                sx={{ minWidth: 220 }}
                            >
                                {connected.map((manager) => (
                                    <MenuItem
                                        key={manager.id}
                                        value={manager.id}
                                    >
                                        {manager.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <Box>
                                <Typography sx={{ fontWeight: 600 }}>{selected.name}</Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    ИИ-менеджер потока
                                </Typography>
                            </Box>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => history.push(`/ai-managers/${selected.id}`)}
                        >
                            Открыть менеджера
                        </Button>
                    </Stack>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            gap: 1,
                            borderTop: '1px solid var(--lm-border)',
                            pt: 2,
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Оффер, цены и ответы на вопросы — в базе знаний менеджера.
                        </Typography>
                        <Button
                            sx={{ flexShrink: 0 }}
                            onClick={() => history.push(`/ai-managers/${selected.id}#knowledge`)}
                        >
                            База знаний ↗
                        </Button>
                    </Box>
                    {managerStreamIds(selected).length > 1 ? (
                        <Alert
                            severity="info"
                            action={
                                <Button onClick={() => history.push(`/sales-funnel?managerId=${selected.id}`)}>
                                    Открыть общую
                                </Button>
                            }
                        >
                            Менеджер ведёт несколько потоков. Его общая воронка доступна отдельно.
                        </Alert>
                    ) : (
                        <ManagerPipeline
                            key={selected.id}
                            manager={selected}
                        />
                    )}
                    {available.length > 0 && (
                        <Box>
                            <Button
                                size="small"
                                onClick={openConnect}
                            >
                                Подключить ещё менеджера
                            </Button>
                        </Box>
                    )}
                </Stack>
            )}
            <Dialog
                open={dialogOpen}
                onClose={() => !connecting && setDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>ИИ-менеджер для потока</DialogTitle>
                <DialogContent>
                    <Stack
                        spacing={2}
                        sx={{ pt: 1 }}
                    >
                        <Typography color="text.secondary">{stream.name || stream.category.name}</Typography>
                        {available.length ? (
                            <TextField
                                select
                                fullWidth
                                label="Выберите менеджера"
                                value={connectId}
                                disabled={connecting}
                                onChange={(event) => setConnectId(event.target.value)}
                            >
                                {available.map((manager) => (
                                    <MenuItem
                                        key={manager.id}
                                        value={manager.id}
                                    >
                                        {manager.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <Typography>Создайте ИИ-менеджера — этот поток уже будет выбран в настройках.</Typography>
                        )}
                        {connectError && <Alert severity="error">{connectError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 2,
                        gap: 1,
                        display: { xs: 'grid', sm: 'flex' },
                        gridTemplateColumns: '1fr 1fr',
                        '& > :not(style) ~ :not(style)': { marginLeft: 0 },
                        '& > :last-child': { gridColumn: available.length ? '1 / -1' : undefined },
                    }}
                >
                    <Button
                        disabled={connecting}
                        onClick={() => setDialogOpen(false)}
                    >
                        Отмена
                    </Button>
                    <Button
                        sx={{ fontSize: 14, px: 1, whiteSpace: 'nowrap' }}
                        variant={available.length ? 'outlined' : 'contained'}
                        disabled={connecting}
                        onClick={() => history.push(`/ai-managers/new?streamId=${stream.id}`)}
                    >
                        Создать нового
                    </Button>
                    {!!available.length && (
                        <Button
                            variant="contained"
                            disabled={connecting || !connectId}
                            onClick={() => void connect()}
                        >
                            {connecting ? 'Подключаем…' : 'Подключить'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </StreamSection>
    );
}

function ManagerPipeline({ manager }: { manager: AIManager }) {
    const history = useHistory();
    const [kanban, setKanban] = useState<AIManagerKanban | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const [movingId, setMovingId] = useState<number | null>(null);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        let live = true;
        setLoading(true);
        setError('');
        AIManagersService.getKanban(manager.id, 80)
            .then((data) => {
                if (live) setKanban(data);
            })
            .catch(() => {
                if (live) setError('Не удалось загрузить воронку.');
            })
            .finally(() => {
                if (live) setLoading(false);
            });
        return () => {
            live = false;
            mounted.current = false;
        };
    }, [manager.id, retry]);
    const move = async (conversation: AIManagerConversation, stage: AIManagerFunnelStageKey) => {
        if (movingId !== null || stage === conversation.salesStage) return;
        setMovingId(conversation.id);
        setError('');
        try {
            const updated = await AIManagersService.moveConversationStage(conversation.id, stage);
            if (mounted.current)
                setKanban((value) =>
                    value ? { ...value, columns: normalizeColumnsAfterMove(value.columns, updated) } : value
                );
        } catch {
            if (mounted.current) setError('Не удалось изменить стадию. Повторите действие.');
        } finally {
            if (mounted.current) setMovingId(null);
        }
    };
    if (loading)
        return (
            <CircularProgress
                size={24}
                aria-label="Загрузка воронки"
            />
        );
    return (
        <Stack spacing={2}>
            {error && (
                <Alert
                    severity="error"
                    action={
                        !kanban ? <Button onClick={() => setRetry((value) => value + 1)}>Повторить</Button> : undefined
                    }
                >
                    {error}
                </Alert>
            )}
            {kanban && (
                <>
                    {!kanban.total && (
                        <Box>
                            <Typography color="text.secondary">
                                Диалогов пока нет. Они появятся после запуска менеджера.
                            </Typography>
                            {manager.status !== 'running' && (
                                <Button onClick={() => history.push(`/ai-managers/${manager.id}`)}>
                                    Настроить менеджера
                                </Button>
                            )}
                        </Box>
                    )}
                    <SalesFunnelBoard
                        columns={kanban.columns}
                        movingConversationId={movingId}
                        onMove={(conversation, stage) => void move(conversation, stage)}
                    />
                </>
            )}
        </Stack>
    );
}
